import { LoadingButton as Button } from '@mui/lab'
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { memo, useCallback, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { IoCheckmarkCircle, IoClose, IoWarning } from 'react-icons/io5'
import { useNavigate } from 'react-router-dom'

import {
  QuestionCardDifficulty,
  QuestionCardOptionsPreview,
  QuestionCardQuestionPreview,
  QuestionCardRoot,
  QuestionCardTag,
  QuestionCardType,
} from '@/components/QuestionCard'
import { useAddNewQuestionMutation } from '@/services/api'
import type { QuestionStorePayload } from '@/types'

interface NewQuestionConfirmProps {
  open: boolean
  onClose?: () => void
  question: QuestionStorePayload
}

function NewQuestionConfirm({ open, question, onClose }: NewQuestionConfirmProps) {
  const theme = useTheme()
  const navigate = useNavigate()
  const [triggerAddNewQuestion, { isLoading, error }] = useAddNewQuestionMutation()
  const [showSuccess, setShowSuccess] = useState(false)

  const questionSummary = useMemo(() => {
    const hasCorrectAnswer = question.options?.some((opt) => opt.its_correct) || false
    const totalTags = [
      ...(question.competence_tags || []),
      ...(question.ability_tags || []),
      ...(question.subject_tags || []),
      ...(question.content_tags || []),
    ].length

    return {
      hasCorrectAnswer,
      totalTags,
      isValid: question.type === 'discursive' || hasCorrectAnswer,
      optionsCount: question.options?.length || 0,
    }
  }, [question])

  const handleSaveNewQuestion = useCallback(async () => {
    if (!questionSummary.isValid) {
      toast.error('Questão inválida. Verifique se há pelo menos uma resposta correta.')
      return
    }

    try {
      await triggerAddNewQuestion(question).unwrap()
      setShowSuccess(true)
      toast.success('Questão criada com sucesso!')

      setTimeout(() => {
        navigate('/exercicios/questoes')
      }, 1500)
    } catch (err) {
      toast.error('Erro ao criar questão. Tente novamente.')
      console.error('Error creating question:', err) // eslint-disable-line
    }
  }, [question, questionSummary.isValid, triggerAddNewQuestion, navigate])

  const handleClose = useCallback(() => {
    if (!isLoading && onClose) {
      onClose()
    }
  }, [isLoading, onClose])

  if (showSuccess) {
    return (
      <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, textAlign: 'center', p: 2 },
        }}>
        <DialogContent>
          <Stack spacing={3} alignItems="center" py={4}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: theme.palette.success.light + '20',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <IoCheckmarkCircle size={40} color={theme.palette.success.main} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Questão criada com sucesso!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Redirecionando para o banco de questões...
            </Typography>
          </Stack>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog
      maxWidth="lg"
      fullWidth
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Revisar Questão
        </Typography>
        <IconButton
          onClick={handleClose}
          disabled={isLoading}
          sx={{ color: 'text.secondary' }}>
          <IoClose />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 0 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 0,
          }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Resumo da Questão
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label={`Tipo: ${question.type === 'multiple choice' ? 'Objetiva' : 'Discursiva'}`}
              color="primary"
              variant="outlined"
              size="small"
            />
            <Chip
              label={`Dificuldade: ${
                question.difficulty === 'easy'
                  ? 'Fácil'
                  : question.difficulty === 'intermediary'
                    ? 'Médio'
                    : 'Difícil'
              }`}
              color="secondary"
              variant="outlined"
              size="small"
            />
            {question.type === 'multiple choice' && (
              <Chip
                label={`${questionSummary.optionsCount} alternativas`}
                color={questionSummary.optionsCount >= 5 ? 'success' : 'warning'}
                variant="outlined"
                size="small"
              />
            )}
            <Chip
              label={`${questionSummary.totalTags} tags`}
              color="info"
              variant="outlined"
              size="small"
            />
            {question.type === 'multiple choice' && (
              <Chip
                label={
                  questionSummary.hasCorrectAnswer ? 'Resposta definida' : 'Sem resposta'
                }
                color={questionSummary.hasCorrectAnswer ? 'success' : 'error'}
                variant="outlined"
                size="small"
                icon={
                  questionSummary.hasCorrectAnswer ? <IoCheckmarkCircle /> : <IoWarning />
                }
              />
            )}
          </Stack>
        </Paper>

        {!questionSummary.isValid && (
          <Alert severity="error" sx={{ m: 3, borderRadius: 2 }}>
            <Typography variant="body2">
              <strong>Atenção:</strong> Esta questão objetiva não possui nenhuma
              alternativa marcada como correta. Adicione pelo menos uma resposta correta
              antes de continuar.
            </Typography>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ m: 3, borderRadius: 2 }}>
            <Typography variant="body2">
              <strong>Erro:</strong> Não foi possível criar a questão. Verifique sua
              conexão e tente novamente.
            </Typography>
          </Alert>
        )}

        <Box sx={{ p: 3 }}>
          <QuestionCardRoot>
            <QuestionCardDifficulty difficulty={question.difficulty} />
            <QuestionCardQuestionPreview question={question} />

            <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
              <QuestionCardType question={question} />
            </Box>

            {question.competence_tags && question.competence_tags.length > 0 && (
              <QuestionCardTag title="Competências" tags={question.competence_tags} />
            )}

            {question.ability_tags && question.ability_tags.length > 0 && (
              <QuestionCardTag title="Habilidades" tags={question.ability_tags} />
            )}

            {question.subject_tags && question.subject_tags.length > 0 && (
              <QuestionCardTag title="Disciplinas" tags={question.subject_tags} />
            )}

            {question.content_tags && question.content_tags.length > 0 && (
              <QuestionCardTag title="Conteúdo" tags={question.content_tags} />
            )}

            {question.options && question.options.length > 0 && (
              <QuestionCardOptionsPreview question={question} />
            )}
          </QuestionCardRoot>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleSaveNewQuestion}
          loading={isLoading}
          disabled={!questionSummary.isValid}
          sx={{
            minWidth: 120,
            borderRadius: 2,
          }}>
          {isLoading ? 'Salvando...' : 'Criar Questão'}
        </Button>

        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isLoading}
          sx={{
            minWidth: 120,
            borderRadius: 2,
          }}>
          Voltar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default memo(NewQuestionConfirm)
